import { useState } from 'react'
import { formatRut, isValidRut, normalizeRut } from '../../utils/rut'

const emptyValues = {
  rut: '',
  nombres: '',
  apellido_paterno: '',
  apellido_materno: '',
  email: '',
  telefono: '',
  direccion: '',
  comuna: '',
  numero_socio: '',
  fecha_ingreso: '',
  estado: 'activo',
  observaciones: '',
}

const inputClass = 'mt-2 w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary outline-none transition placeholder:text-text-secondary/60 focus:border-secondary focus:ring-4 focus:ring-secondary/15'
const labelClass = 'block text-sm font-medium text-text-primary'

export default function SocioForm({ initialValues, onSubmit, onCancel, submitLabel }) {
  const [values, setValues] = useState({ ...emptyValues, ...initialValues, rut: formatRut(initialValues?.rut) })
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [submitError, setSubmitError] = useState('')

  function update(field, value) {
    setValues((current) => ({ ...current, [field]: value }))
    if (errors[field]) setErrors((current) => ({ ...current, [field]: '' }))
  }

  function validate() {
    const nextErrors = {}
    if (!isValidRut(values.rut)) nextErrors.rut = 'Ingresa un RUT chileno válido.'
    if (!values.nombres.trim()) nextErrors.nombres = 'Los nombres son obligatorios.'
    if (!values.apellido_paterno.trim()) nextErrors.apellido_paterno = 'El apellido paterno es obligatorio.'
    if (!values.fecha_ingreso) nextErrors.fecha_ingreso = 'La fecha de ingreso es obligatoria.'
    if (values.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) nextErrors.email = 'Ingresa un correo válido.'
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitError('')
    if (!validate() || saving) return

    setSaving(true)
    try {
      await onSubmit({
        ...values,
        rut: normalizeRut(values.rut),
        nombres: values.nombres.trim(),
        apellido_paterno: values.apellido_paterno.trim(),
        apellido_materno: values.apellido_materno.trim(),
        email: values.email.trim().toLowerCase(),
        telefono: values.telefono.trim(),
        direccion: values.direccion.trim(),
        comuna: values.comuna.trim(),
        numero_socio: values.numero_socio.trim(),
        observaciones: values.observaciones.trim(),
      })
    } catch (error) {
      setSubmitError(error.message)
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-8">
      {submitError && <div className="rounded-xl border border-danger/25 bg-danger/5 p-4 text-sm text-danger" role="alert">{submitError}</div>}

      <section className="rounded-2xl border border-border bg-surface p-5 shadow-sm sm:p-6">
        <div className="border-b border-border pb-4"><h2 className="text-lg font-semibold">Información personal</h2><p className="mt-1 text-sm text-text-secondary">Datos de identificación del socio.</p></div>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <label className={labelClass}>RUT <span className="text-danger">*</span><input value={values.rut} onChange={(event) => update('rut', formatRut(event.target.value))} onBlur={() => { if (values.rut && !isValidRut(values.rut)) setErrors((current) => ({ ...current, rut: 'Ingresa un RUT chileno válido.' })) }} placeholder="12.345.678-5" className={inputClass} aria-invalid={Boolean(errors.rut)} />{errors.rut && <span className="mt-1.5 block text-xs text-danger">{errors.rut}</span>}</label>
          <label className={labelClass}>Nombres <span className="text-danger">*</span><input value={values.nombres} onChange={(event) => update('nombres', event.target.value)} className={inputClass} aria-invalid={Boolean(errors.nombres)} />{errors.nombres && <span className="mt-1.5 block text-xs text-danger">{errors.nombres}</span>}</label>
          <label className={labelClass}>Apellido paterno <span className="text-danger">*</span><input value={values.apellido_paterno} onChange={(event) => update('apellido_paterno', event.target.value)} className={inputClass} aria-invalid={Boolean(errors.apellido_paterno)} />{errors.apellido_paterno && <span className="mt-1.5 block text-xs text-danger">{errors.apellido_paterno}</span>}</label>
          <label className={labelClass}>Apellido materno<input value={values.apellido_materno} onChange={(event) => update('apellido_materno', event.target.value)} className={inputClass} /></label>
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-surface p-5 shadow-sm sm:p-6">
        <div className="border-b border-border pb-4"><h2 className="text-lg font-semibold">Contacto</h2><p className="mt-1 text-sm text-text-secondary">Información opcional para mantener el contacto.</p></div>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <label className={labelClass}>Email<input type="email" value={values.email} onChange={(event) => update('email', event.target.value)} placeholder="nombre@correo.cl" className={inputClass} aria-invalid={Boolean(errors.email)} />{errors.email && <span className="mt-1.5 block text-xs text-danger">{errors.email}</span>}</label>
          <label className={labelClass}>Teléfono<input type="tel" value={values.telefono} onChange={(event) => update('telefono', event.target.value)} placeholder="+56 9 1234 5678" className={inputClass} /></label>
          <label className={`${labelClass} sm:col-span-2`}>Dirección<input value={values.direccion} onChange={(event) => update('direccion', event.target.value)} className={inputClass} /></label>
          <label className={labelClass}>Comuna<input value={values.comuna} onChange={(event) => update('comuna', event.target.value)} className={inputClass} /></label>
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-surface p-5 shadow-sm sm:p-6">
        <div className="border-b border-border pb-4"><h2 className="text-lg font-semibold">Datos de socio</h2><p className="mt-1 text-sm text-text-secondary">Información administrativa dentro del Centro de Padres.</p></div>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <label className={labelClass}>Número de socio<input value={values.numero_socio} onChange={(event) => update('numero_socio', event.target.value)} className={inputClass} /></label>
          <label className={labelClass}>Fecha de ingreso <span className="text-danger">*</span><input type="date" value={values.fecha_ingreso} onChange={(event) => update('fecha_ingreso', event.target.value)} className={inputClass} aria-invalid={Boolean(errors.fecha_ingreso)} />{errors.fecha_ingreso && <span className="mt-1.5 block text-xs text-danger">{errors.fecha_ingreso}</span>}</label>
          <label className={labelClass}>Estado<select value={values.estado} onChange={(event) => update('estado', event.target.value)} className={inputClass}><option value="activo">Activo</option><option value="pendiente">Pendiente</option><option value="inactivo">Inactivo</option></select></label>
          <label className={`${labelClass} sm:col-span-2`}>Observaciones<textarea value={values.observaciones} onChange={(event) => update('observaciones', event.target.value)} rows="4" className={inputClass} /></label>
        </div>
      </section>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <button type="button" onClick={onCancel} disabled={saving} className="rounded-xl border border-border bg-surface px-5 py-3 font-semibold text-text-primary transition hover:border-secondary hover:text-primary disabled:opacity-60">Cancelar</button>
        <button type="submit" disabled={saving} className="rounded-xl bg-primary px-5 py-3 font-semibold text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60">{saving ? 'Guardando…' : submitLabel}</button>
      </div>
    </form>
  )
}
